import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {
  emptyContract,
  emptyContractData,
  emptyService,
  emptySubscription,
  emptySubscriptionRequest,
  IContract, IContractData,
  IData,
  IService,
  ISubscription,
  ISubscriptionRequest, TServiceStatus,
  TServiceType
} from "@retail-data-factory/rdf-angular-types-api";
import {emptyServiceResponse, IServiceResponse} from "../../../../services/external-api/response-interfaces";
import {DataService} from "../../../../services/external-api/data.service";
import * as moment from "moment";
import {map} from "rxjs/operators";
import {emptyResource, IParty, IPlatformConnectionRequestServiceConnecting, IResource, IResourceGroup} from "@retail-data-factory/rdf-angular-types-api";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";
import {AssistTranslationService} from "@retail-data-factory/rdf-angular-translation-service";
import {Presenter} from "../../../internal/presenter/presenter";
import {emptyMDMElement, IMDMElement} from "@retail-data-factory/rdf-angular-types-api";

@Injectable({
  providedIn: 'root'
})
export class ApiMarketplaceService {

  constructor(
    private dataService: DataService,
    private presenter: Presenter,
    private atService: AssistTranslationService,
    private configurationManager: ConfigurationManager
  ) {
  }

  getMarketplaceApplications(): Observable<Array<IResourceGroup>> {
    return of([]);

  }

  /**
   * Получить список ресурсов модуля маркетплейс, которые может видеть пользователь
   */
  getMarketplaceResources(): Observable<Array<IResource>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/resources`, {}).pipe(
      map(result => {
          const resources: Array<IResource> = result.report.map(item => this.mapMarketplaceResourceFromService(item));
          return resources;
        }
      )
    );
  }

  /**
   * Список доступных сервисов
   * @param resourceId - показывать только сервисы с присутствующим в них ресурсом
   */
  getServices(resourceId?: string): Observable<Array<IService>> {
    const header = resourceId===undefined || resourceId === null?{}:{resource_id: resourceId}
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services`, header).pipe(
      map(result => {
          const services: Array<IService> = result.report.map(item => this.mapMarketplaceServiceFromService(item));
          return services;
        }
      )
    );

  }

  getService(serviceId: string): Observable<IService> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            const service: IService = this.mapMarketplaceServiceFromService(result.report[0]);
            return service;
          } else {
            return {...emptyService};
          }
        }
      )
    );
  }

  createService(service: IService): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services`, {}, this.mapMarketplaceServiceToService(service));
  }

  updateService(service: IService): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${service.id}`, {}, this.mapMarketplaceServiceToService(service));
  }
  saveService(service: IService):Observable<IServiceResponse> {
    return service.id?
      this.updateService(service):
      this.createService(service);
  }

  publishService(serviceId: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/publication`,{},'');
  }
  unpublishService(serviceId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/publication`,{},'');
  }


  deleteService(serviceId: string): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }


  getSubscribedServices(partyId: string): Observable<Array<IService>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscribers/${partyId}/subscriptions`, {}).pipe(
      map(result => {
          const tmp = partyId;
          const subscriptions: Array<ISubscription> = result.report.map(item => this.mapSubscriptionFromService(item));
          const services: Array<IService> = [];
          subscriptions.forEach(item => {
            if (item.subscriberPartyId === partyId) {
              services.push(item.service);
            }
          });
          return services;
        }
      )
    );
  }

  /**
   * Получить список сервисов которые связаны с определенным типом проекта
   * @param typeCode
   */
  getServicesForProjectType(typeCode: string): Observable<Array<IService>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiPlatformUrl')}/projecttypes/${typeCode}/services`, {}).pipe(
      map(result => {
          const services: Array<IService> = result.report.map(item => this.mapMarketplaceServiceFromService(item));
          return services;
        }
      )
    );
  }

  setServiceAvailable(serviceId: string, partiId: string): Observable<IServiceResponse> {

    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/availabilty/${partiId}`,{},null);
  }


  getSubscribedSubscriptions(partyId: string): Observable<Array<ISubscription>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscribers/${partyId}/subscriptions`, {}).pipe(
      map(result => {
          const subscriptions: Array<ISubscription> = result.report.map(item => this.mapSubscriptionFromService(item));
          return subscriptions;
        }
      )
    );
  }


  createSubscriptionRequest(serviceId: string, profile: object = {}): Observable<IServiceResponse> {
    const param = {
      'service_id': serviceId,
      'type_code': 'SUBSCRIBE'

    };
    return this.dataService.postData(this.configurationManager.getApiUrl('apiMarketplaceUrl') + '/requests', param, JSON.stringify(profile));
  }

  createUnsubscriptionRequest(subscription: ISubscription): Observable<IServiceResponse> {
    const param = {
      'service_id': subscription.service.id,
      'type_code': 'UNSUBSCRIBE',
      'subscription_id': subscription.id,

    };
    const body = {};
    return this.dataService.postData(this.configurationManager.getApiUrl('apiMarketplaceUrl') + '/requests', param, body);
  }

  approveRequest(requestId): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/requests/${requestId}/approve`, {}, {});
  }

  getSubscriptionRequests(partyId?: string): Observable<Array<ISubscriptionRequest>> {
    const header = partyId ? {'party_id': partyId} : {};
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/requests`, header).pipe(
      map(result => {
          const requests: Array<ISubscriptionRequest> = result.report.map(item => this.mapRequestFromService(item));
          return requests;
        }
      )
    );
  }

  getSubscriptionRequest(requestId: string): Observable<ISubscriptionRequest> {
    return this.dataService.getReportData(this.configurationManager.getApiUrl('apiMarketplaceUrl') + `/requests/${requestId}`, {}).pipe(
      map(
        data => {
          if (data && data.report && data.report.length > 0) {
            const item = data.report[0];
            const service: IService = {
              ...emptyService,
              id: item['service_id'], //ID сервиса
              type: item['service_type'], // Тип сервиса
              code: item['service_code'], // Код сервиса присвоенный владельцем
              name: item['service_name'], // Название сервиса присвоенный владельцем
              price: 0,
              longName: '', // Длинное название сервиса
              description: item['service_description'], // Описание сервиса
              ownerPartyId: item['service_owner_id'], // ID владельца
              ownerPartyName: item['service_owner_name'], // Название владельца
              ownerPartyShortName: item['service_owner_short_name'] ? item['service_owner_short_name'] : '',
              dateCreate: null, // Дата создания сервиса
              dateStart: moment(item['service_start_date'], 'YYYYMMDD').toDate(), // Дата начала действия сервиса
              dateEnd: moment(item['service_end_date'], 'YYYYMMDD').toDate(), //  Дата окончания действия сервиса
              status: null, // Статус
              tags: [], // Массив тегов
              resources: [],  // Список ресурсов, к которым предоставляется доступ в рамках сервиса
              publishedTo: [],
              subscriptionID: null,
              profile: item['service_profile'],
              isDataLoaded: true
            };
            const request: ISubscriptionRequest = {
              ...emptySubscriptionRequest,
              id: item['request_id'],
              requestDate: moment(item['request_date'], 'YYYYMMDD').toDate(),
              statusCode: item['request_status_code'],
              type: item['request_type_code'],
              subscriberPartyId: item['subscriber_party_id'],
              subscriberPartyName: item['subscriber_party_name'],
              profile: item['profile'],
              service: service
            };
            return request;

          }
          else {
            return {...emptySubscriptionRequest};
          }
        }
      ));
  }


  getSubscriptions(): Observable<Array<ISubscription>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions`, {}).pipe(
      map(result => {
          const subscriptions: Array<ISubscription> = result.report.map(item => this.mapSubscriptionFromService(item));
          return subscriptions;
        }
      )
    );
  }

  getSubscriptionsDS(service: DataService, offset: number, limit: number, searchStr: string, param: object, configurationManager: ConfigurationManager): Observable<IServiceResponse> {
    const aaa = this;
    const bbb = service;
    if (searchStr) {
      const encodedSearchStr = searchStr;
      return service.getReportData(`${configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions?offset=${offset}&limit=${limit}&search=${encodedSearchStr}`, {});
    } else {
      return service.getReportData(`${configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions?offset=${offset}&limit=${limit}`, {});

    }
  }

  getSubscription(subscriptionId: string): Observable<ISubscription> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscriptionId}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            const subscription: ISubscription = this.mapSubscriptionFromService(result.report[0]);
            return subscription;
          } else {
            return {...emptySubscription};
          }
        }
      )
    );
  }


  createSubscription(subscription: ISubscription): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions`, {}, this.mapSubscriptionToService(subscription));
  }
  updateSubscription(subscription: ISubscription): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscription.id}`, {}, this.mapSubscriptionToService(subscription));
  }
  saveSubscription(subscription: ISubscription): Observable<IServiceResponse> {
    return subscription.id?
      this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscription.id}`, {}, this.mapSubscriptionToService(subscription)):
      this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscription.id}`, {}, this.mapSubscriptionToService(subscription));
  }
  deleteSubscriptionsForProject(projectId): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/projects/${projectId}`, {});
  }


  createSubscriptions(party: IParty, services: Array<IService>, attributes: Array<IData>): Observable<IServiceResponse> {
    const param = {
      party_id: party.id
    };
    const body = {
      services: services.map(t => t.id),
      attributes: attributes.map(t => ({type: t.code, values: t.values.map(a => a.code)}))
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions`, param, body);
  }

  createSubscriber(party: IParty, services: Array<IService>, attributes: Array<IData>): Observable<IServiceResponse> {
    const param = {
      email: party.email,

    };
    const body = {
      login: party.email,
      name: party.name,
      inn: party.legalId,
      services: services.map(t => t.id),
      attributes: attributes.map(t => ({type: t.code, values: t.values.map(a => a.code)}))
    };
    if (this.configurationManager.theme === 'auchan') {
      return this.dataService.postData(`${this.configurationManager.getApiUrl('apiPlatformUrl')}/users`, param, body);
    } else {
      return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscribers`, param, body);
    }
  }


  prolongSubscription(subscription: ISubscription): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscription.id}/prolong`, {}, {date_end: moment(subscription.dateEnd).format('YYYYMMDD')});

  }

  deleteSubscription(subscriptionId: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/${subscriptionId}/cancel`, {}, null);
  }

  removeAllSubscriptionsFromParty(partyId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscribers/${partyId}/subscriptions`, {});
  }


  getSubscriptionContracts(): Observable<Array<IContract>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/subscriptions/contracts`, {}).pipe(
      map(result => {
          const contracts: Array<IContract> = result.report.map(item => this.mapContractFromService(item));
          return contracts;
        }
      )
    );
  }
  getContracts(): Observable<Array<IContract>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts`, {}).pipe(
      map(result => {
          const contracts: Array<IContract> = result.report.map(item => this.mapContractFromService(item));
          return contracts;
        }
      )
    );
  }
  getContract(contractId: string): Observable<IContract> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          const contract: IContract = this.mapContractFromService(result.report[0]);
          return contract;
        } else {
          return {...emptyContract};
        }

        }
      )
    );
  }
  saveContract(contract: IContract):Observable<IServiceResponse> {
    return contract.id?this.updateContract(contract):this.createContract(contract);
  }
  createContract(contract: IContract):Observable<IServiceResponse> {
    const body = {
      contract_type_code: contract.typeCode,
      contract_status: contract.statusCode,
      contract_name: contract.name,
      contract_description: contract.description,
      profile: {}
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts`, {}, body);

  }
  updateContract(contract: IContract):Observable<IServiceResponse> {
    const body = {
      contract_type_code: contract.typeCode,
      contract_status: contract.statusCode,
      contract_name: contract.name,
      contract_description: contract.description,
      profile: {}
    };

    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contract.id}`, {}, body);

  }
  deleteContract(contractId: string):Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}`, {});
  }

  getContractDatas(contractId: string): Observable<Array<IContractData>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}/data`, {}).pipe(
      map(result => {
          const contracts: Array<IContractData> = result.report.map(item => this.mapContractDataFromService(item));
          return contracts;
        }
      )
    );
  }
  getContractData(contractId: string, contractDataId:string): Observable<IContractData> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}/data/${contractDataId}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            const contractData: IContractData = this.mapContractDataFromService(result.report[0]);
            return contractData;
          } else {
            return {...emptyContractData};
          }
        }
      )
    );
  }
  createContractData(contractId: string, contractData: IContractData):Observable<IServiceResponse> {
    const body =
    {
      contract_data_type: contractData.typeCode,
      contract_data_status: contractData.statusCode,
      contract_data_name: contractData.name,
      contract_data_description: contractData.description,
      url: contractData.url,
      date_start: moment(contractData.dateFrom).format('YYYYMMDD'),
      date_end: moment(contractData.dateTo).format('YYYYMMDD'),
      profile: contractData.profile?contractData.profile:{}
  };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}/data`, {}, body);

  }
  updateContractData(contractId: string, contractData: IContractData):Observable<IServiceResponse> {
    const body =
      {
        contract_data_type: contractData.typeCode,
        contract_data_status: contractData.statusCode,
        contract_data_name: contractData.name,
        contract_data_description: contractData.description,
        url: contractData.url,
        date_start: moment(contractData.dateFrom).format('YYYYMMDD'),
        date_end: moment(contractData.dateTo).format('YYYYMMDD'),
        profile: contractData.profile?contractData.profile:{}
      };
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}/data/${contractData.id}`, {}, body);

  }
  deleteContractData(contractId: string, contractDataId:string):Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/contracts/${contractId}/data/${contractDataId}`, {});
  }


  getServiceContracts(serviceId: string): Observable<Array<IContract>> {
    if (!serviceId) return of([]);
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/contracts`, {}).pipe(
      map(result => {
          const contracts: Array<IContract> = result.report.map(item => this.mapContractFromService(item));
          return contracts;
        }
      )
    );
  }

  createServiceContract(serviceId: string, contractId: string):Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/contracts/${contractId}`, {}, null);
  }
  deleteServiceContract(serviceId: string, contractId: string):Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiMarketplaceUrl')}/services/${serviceId}/contracts/${contractId}`, {});
  }


  prepareSubscriptionFromService(subscriberParty: IParty, service: IService, attributes: Array<IData>): ISubscription {
    return {
      ...emptySubscription,
      id: '',
      dateCreate: null,
      dateStart: null,
      dateEnd: null,
      status: 'ACTIVE',
      subscriberPartyId: subscriberParty.id,
      subscriberPartyName: subscriberParty.name,
      request: emptySubscriptionRequest,
      service: service,
      profile: null,
      resources: [],
      data: attributes
    };
  }

  mapMarketplaceServiceFromService(item: object): IService {
    const service: IService = {
      ...JSON.parse(JSON.stringify(emptyService)),
      id: item['service_id'].toString(), //ID сервиса
      type: item['service_type'], // Тип сервиса
      code: item['service_code'], // Код сервиса присвоенный владельцем
      name: item['service_name'], // Название сервиса присвоенный владельцем
      longName: item['profile'] && item['profile']['long_service_name'] ? item['profile']['long_service_name'] : '', // Длинное название сервиса
      showPrice: item['profile'] && item['profile']['show_price'],
      price: item['profile'] && item['profile']['price'] ? item['profile']['price'] : 0, // Длинное название сервиса
      priceType: item['profile'] && item['profile']['price_type'] ? item['profile']['price_type'] : 'COMMON',
      currencyCode: item['profile'] && item['profile']['currency_code'] ? item['profile']['currency_code'] : 'RUB',
      description: item['service_description'], // Описание сервиса
      ownerPartyId: item['party_id'].toString(), // ID владельца
      ownerPartyName: item['party_name'], // Название владельца
      ownerPartyShortName: item['party_short_name'] ? item['party_short_name'] : '', // Короткое название владельца
      dateCreate: moment(item['date_create'], 'YYYYMMDD').toDate(), // Дата создания сервиса
      dateStart: moment(item['date_start'], 'YYYYMMDD').toDate(), // Дата начала действия сервиса
      dateEnd: moment(item['date_end'], 'YYYYMMDD').toDate(), //  Дата окончания действия сервиса
      status: item['service_status'], // Статус
      tags: item['service_tags'], // Массив тегов
      resources: [],  // Список ресурсов, к которым предоставляется доступ в рамках сервиса,
      roles: [], //Список ролей в сервисе
      attributeTypes: [],
      publishedTo: item['published_to'].map(t => ({id: t['party_id'].toString(), name: t['party_name'], profile: null})),
      subscriptionID: item['subscription_id'],
      profile: item['profile'],
      isDataLoaded: true
    };
    if (item['service_resources'] && Array.isArray(item['service_resources'])) {
      service.resources = item['service_resources'].map(t => (
        {
          ...JSON.parse(JSON.stringify(emptyResource)),
          id: t['resource_id'] ? t['resource_id'].toString() : '',
          code: t['resource_code'],
          name: t['resource_name'],
          isTransferable: t['is_transferable'],
          groupId: t['application_id'] ? t['application_id'].toString() : '',
          groupCode: t['application_code'],
          groupName: t['application_name'],
          profile: t['resource_profile'],
          resourceGroupProfile: t['application_profile']
        }
      ));
    }
    if (Array.isArray(item['service_roles'])) {
      service.roles = item['service_roles'].map(t=>(
        {
          id: t['role_id']?t['role_id'].toString():'',
          code: t['role_code']?t['role_code']:'',
          name: t['role_name']?t['role_name']:'',
          description: t['role_description']?t['role_description']:'',
          resources: Array.isArray(t['resources'])? t['resources'].map(k=>(
            {
              ...JSON.parse(JSON.stringify(emptyResource)),
              id: k['resource_id'] ? k['resource_id'].toString() : '',
              code: k['resource_code'],
              name: k['resource_name'],

              groupId: k['application_id'] ? k['application_id'].toString() : '',
              groupCode: k['application_code'],
              groupName: k['application_name']
            }
          )):[]

        }
      ));
    }
    if (item['service_attribute_types'] && Array.isArray(item['service_attribute_types'])) {
      service.attributeTypes = item['service_attribute_types'].map(t => {
        const tmp =
          {
            code: t['type_code'],
            name: t['type_name'],
            description: t['type_description'],
            levelNum: t['level_num'],
            profile: t['profile']
          };
        return tmp;
      }
      );
    }

    return service;
  }

  mapMarketplaceServiceToService(service: IService): object {
    const result = {
      service_id: service.id,
      service_type: service.type,
      service_code: service.code,
      service_name: service.name,

      service_description: service.description,
      date_start: moment(service.dateStart).format('YYYYMMDD'),
      date_end: moment(service.dateEnd).format('YYYYMMDD'),
      status: service.status,
      tags: service.tags,
//      service_resources: service.resources.map(resource=>({id: resource.id, code: resource.code, profile: resource.profile})),
      service_resources: service.resources.map(resource => ({id: resource.id, code: resource.code, is_transferable: resource.isTransferable})), //Профиль убран в рамках исправления case 38460
      service_attribute_types: service.attributeTypes.map( type =>({type_code: type.code,type_name: type.name,type_description: type.description, level_num: type.levelNum, profile: type.profile  })),
      published_to: service.publishedTo.map(party => party.id),
      profile: service.profile
    };
    if (!result['profile']) {
      result['profile'] = {
        'long_service_name': service.longName,
        'show_price': !!service.showPrice,
        'price': service.price?service.price: 0,
        'price_type': service.priceType?service.priceType: 'COMMON',
        'currency_code': service.currencyCode

      };
    } else {
      result['profile']['long_service_name'] = service.longName;
      result['profile']['show_price'] = !!service.showPrice;
      result['profile']['price'] = service.price?service.price: 0;
      result['profile']['price_type'] = service.priceType?service.priceType: 'COMMON';
      result['profile']['currency_code'] = service.currencyCode;

    }
    return result;
  }

  mapRequestFromService(item: object): ISubscriptionRequest {
    const service: IService = {
      ...emptyService,
      id: item['service_id'] !== null && item['service_id'] !== undefined ? item['service_id'].toString() : '', //ID сервиса
      type: item['service_type'], // Тип сервиса
      code: item['service_code'], // Код сервиса присвоенный владельцем
      name: item['service_name'], // Название сервиса присвоенный владельцем
      price: 0,
      longName: '', // Длинное название сервиса
      description: item['service_description'], // Описание сервиса
      ownerPartyId: item['service_owner_id'].toString(), // ID владельца
      ownerPartyName: item['service_owner_name'], // Название владельца
      ownerPartyShortName: item['service_owner_short_name'] ? item['service_owner_short_name'] : '',
      dateCreate: null, // Дата создания сервиса
      dateStart: moment(item['service_start_date'], 'YYYYMMDD').toDate(), // Дата начала действия сервиса
      dateEnd: moment(item['service_end_date'], 'YYYYMMDD').toDate(), //  Дата окончания действия сервиса
      status: null, // Статус
      tags: [], // Массив тегов
      resources: [],  // Список ресурсов, к которым предоставляется доступ в рамках сервиса
      publishedTo: [],
      subscriptionID: item['subscription_id'] != null && item['subscription_id'] !== undefined ? item['subscription_id'].toString() : '',
      profile: null,
      isDataLoaded: true

    };
    const request: ISubscriptionRequest = {
      id: item['request_id'] !== null && item['request_id'] !== undefined ? item['request_id'].toString() : '',
      requestDate: moment(item['request_date'], 'YYYYMMDD').toDate(),
      type: item['request_type_code'] ? item['request_type_code'] : 'UNDEFINED',
      statusCode: item['request_status_code'],
      subscriberPartyId: item['subscriber_party_id'].toString(),
      subscriberPartyName: item['subscriber_party_name'],
      subscriberEmail: item['user_create_email']? item['user_create_email'] : '',
      subscriberFirstName: item['user_create_first_name']  ? item['user_create_first_name'] : '',
      subscriberLastName: item['user_create_last_name']  ? item['user_create_last_name'] : '',
      profile: item['profile'],
      service: service,
    };
    return request;
  }


  mapSubscriptionFromService(item: object): ISubscription {
    const service: IService = {
      ...emptyService,
      id: item['service_id'].toString(), //ID сервиса
      type: item['service_type'], // Тип сервиса
      code: item['service_code'], // Код сервиса присвоенный владельцем
      name: item['service_name'], // Название сервиса присвоенный владельцем
      longName: item['service_profile'] && item['service_profile']['long_service_name'] ? item['service_profile']['long_service_name'] : '', // Длинное название сервиса
      price: item['service_profile'] && item['service_profile']['price'] ? item['service_profile']['price'] : 0, // Длинное название сервиса
      description: item['service_description'], // Описание сервиса
      ownerPartyId: item['service_owner_id'].toString(), // ID владельца
      ownerPartyName: item['service_owner_name'], // Название владельца
      dateCreate: null, // Дата создания сервиса
      dateStart: null, // Дата начала действия сервиса
      dateEnd: null, //  Дата окончания действия сервиса
      status: null, // Статус
      tags: [], // Массив тегов
      resources: item['subscription_resources'] ? item['subscription_resources'].map(t => ({
        id: t['resource_id'].toString(),
        code: t['resource_code'],
        name: t['resource_name'],
        description: '',
        url: '',
        img: '',
        type: '',
        groupId: t['application_id'].toString(),
        groupCode: t['application_code'],
        groupName: t['application_name'],
        profile: t['resource_profile'],
        resourceGroupProfile: t['application_profile'],
      })) : [],  // Список ресурсов, к которым предоставляется доступ в рамках сервиса
      publishedTo: [],
      subscriptionID: item[''],
      profile: item['profile']
    };
    service.attributeTypes = item['service_attribute_types'] && Array.isArray(item['service_attribute_types'])?
      item['service_attribute_types'].map(t=>({
        code: t['attr_type'],
        name: '',
        description: '',
        levelNum: t['attr_level']?t['attr_level'].toString():'0',
        profile: ''
      })):[];


    const request: ISubscriptionRequest = {
      ...emptySubscriptionRequest,
      id: item['request_id'] !== undefined && item['request_id'] !== null ? item['request_id'].toString() : '',
      requestDate: moment(item['request_date'], 'YYYYMMDD').toDate(),
      statusCode: 'APPROVED',
      subscriberPartyId: item['subscriber_party_id'].toString(),
      subscriberPartyName: item['subscriber_party_name'],
      profile: null,
      service: service
    };
    const subscription: ISubscription = {
      ...emptySubscription,
      id: item['subscription_id'].toString(),
      projectId: item['project_id']?item['project_id'].toString():'',
      dateCreate: moment(item['subscription_create_date'], 'YYYYMMDD').toDate(),
      dateStart: moment(item['subscription_start_date'], 'YYYYMMDD').toDate(),
      dateEnd: moment(item['subscription_end_date'], 'YYYYMMDD').toDate(),
      status: item['subscription_status'],
      subscriberPartyId: item['subscriber_party_id'].toString(),
      subscriberPartyName: item['subscriber_party_name'],
      request: request,
      service: service,
      profile: item['profile'],
      resources: service.resources,
      data: [],
    };
    if (moment().isAfter(moment(item['subscription_end_date'], 'YYYYMMDD'),'day') && subscription.status === 'ACTIVE') {
      subscription.status = 'EXPIRED';
    }

    if (item['subscription_attributes'] && Array.isArray(item['subscription_attributes'])){
      item['subscription_attributes'].forEach(t=>{
        let fnd:IData = subscription.data.find(k=>k.code === t.attr_type);
        if (!fnd) {
          fnd = {
            code: t.attr_type,
            level: t.attr_level,
            name: '',
            values: []
          };
          subscription.data.push(fnd);
        }
        fnd.values.push({
          ...emptyMDMElement,
          code: t['attr_code'],
//          name: `(${t['attr_code']}) ${t['attr_name']}`,
          name: `${t['attr_name']}`,
          description: t['attr_description'],
        });

      });
    }


    /*
    //todo: Нужен рефакторинг - убрать эти 2 прохода и сделать один цикл
    const suppliers: IData = {
      code: 'RED_SUPPLIERS', //Код типа
      level: 2,
      name: 'Suppliers',
      values: item['subscription_attributes']
        .filter(t => t.attr_type === 'RED_SUPPLIERS')
        .map(t => ({
          code: t['attr_code'],
          name: `${t['attr_name']}`,
          description: t['attr_description'],
          parentCode: null,
          parentName: null,
          parents: [],
          profile: t

        }))
    };
    subscription.data.push(suppliers);

    const time: IData = {
      code: 'RED_TIME', //Код типа
      level: 2,
      name: 'Time',
      values: item['subscription_attributes']
        .filter(t => t.attr_type === 'RED_TIME')
        .map(t => ({
          code: t['attr_code'],
          name: t['attr_name'],
          description: t['attr_description'],
          parentCode: null,
          parentName: null,
          parents: [],
          profile: t

        }))
    };
    subscription.data.push(time);
*/
    return subscription;
  }

  mapSubscriptionToService(subscription: ISubscription): object {
    const data: object = {
      subscription_id: subscription.id,
      service_id: subscription.service.id,
      request_id: subscription.request.id,
      project_id: subscription.projectId,
      subscriber_party_id: subscription.subscriberPartyId,
      subscription_attributes: [],
      subscription_status: subscription.status,
      subscription_create_date: moment(subscription.dateCreate).format('YYYYMMDD'),
      subscription_start_date: moment(subscription.dateStart).format('YYYYMMDD'),
      subscription_end_date: moment(subscription.dateEnd).format('YYYYMMDD'),
      profile: subscription.profile
    };
    subscription.data.forEach(type => {
      type.values.forEach(value => {
        data['subscription_attributes'].push({
          attr_type: type.code,
          attr_code: value.code,
          attr_name: value.profile['attr_name'],
          attr_description: value.profile['attr_description'],

        });
      });

    });


    return data;
  }

  mapMarketplaceResourceFromService(item: object): IResource {
    return {
      ...emptyResource,

      id: item['Resource ID'],
      code: item['Resource Code'],
      name: item['Resource Name'],
      description: item['Resource Description'],
      url: item['Application URL'],
      img: item['Resource Icon'],
      type: item['Resource Type'],

      groupId: item['Application ID'],
      groupCode: item['Application Code'],
      groupName: item['Application Name'],
      profile: item['resource_profile']
    };
  }


  decodeServiceIcon(typeType: TServiceType): string {
    switch (typeType) {
      case 'INSIGHT':
        return 'bar-icon-marketplace.png';
      case 'APPLICATION':
        return 'bar-icon-marketplace.png';
      case 'API':
        return 'services-icon-marketplace.png';
      case 'MDM':
        return 'services-icon-marketplace.png';
      default:
        return '';
    }
  }

  decodeServiceTypeName(typeType: TServiceType): string {
    switch (typeType) {
      case "INSIGHT":
        return this.atService.translateService.instant('marketplace.marketplace-report.Insight');
      case "API":
        return this.atService.translateService.instant('marketplace.marketplace-report.Data Services');
      case "MDM":
        return this.atService.translateService.instant('marketplace.marketplace-report.Insight');
      case "APPLICATION":
        return 'Приложение';
      case "UNDEFINED":
        return 'Не определен';
      default:
        return 'Другое';
    }
  }

  mapContractFromService(item: object): IContract {
    const contract: IContract = {
      ...emptyContract,
      id: item['contract_id'].toString(),
      name: item['contract_name'] ? item['contract_name'] : '',
      description: item['contract_description'] ? item['contract_description'] : '',
      typeCode: item['contract_type_code'] ? item['contract_type_code'] : '',
      statusCode: item['contract_status'] ? item['contract_status'] : 'ACTIVE',
      data: item['contract_data'] && Array.isArray(item['contract_data']) ? item['contract_data'].map(t => ({
        ...emptyContractData,
        id: t['data_id'].toString(),
        name: t['data_name'] ? t['data_name'] : '',
        description: t['data_description'] ? t['data_description'] : '',
        typeCode: t['data_type_code'] ? t['data_type_code'] : '',
        statusCode: t['data_status_code'] ? t['data_status_code'] : '',
        dateFrom: t['date_from'] ? moment(t['date_from'], 'YYYYMMDD HH:mm:ss').toDate() : null,
        dateTo: t['date_to'] ? moment(t['date_to'], 'YYYYMMDD HH:mm:ss').toDate() : null,
        url: t['url'] ? t['url'] : '',
      })) : []
    };
    return contract;
  }
  mapContractDataFromService(item: object):IContractData {
    return {
      ...emptyContractData,
      id: item['contract_data_id'].toString(),
      name: item['contract_data_name'],
      description: item['contract_data_description'],
      typeCode: item['contract_data_type'],
      statusCode: item['contract_data_status'],
      dateFrom:  item['date_start'] ? moment(item['date_start'], 'YYYYMMDD').toDate() : null,
      dateTo:  item['date_end'] ? moment(item['date_end'], 'YYYYMMDD').toDate() : null,
      url: item['url'],
      profile: item['profile']
    };
  }
  decodeContractDataStatusColor(statusCode: string): string {
    switch (statusCode) {
      case 'ACTIVE': return 'green';
      case 'DRAFT': return 'gray';
      default: return 'gray';
    }
  }

  decodeServicePrice(service: IService): string {
    if (!service) {
      return this.atService.instant('common.error!');//'Ошибка!';
    }


    switch (service.priceType) {
      case "COMMON":     return service.showPrice?
        service.price ? `${service.price} ${this.atService.instant(`currency.${service.currencyCode}`)}/${this.atService.instant('common.month')}` :this.atService.instant('common.free')
        : '';
      case "STORE-DAY":  return service.showPrice?
        service.price ? `${service.price} ${this.atService.instant(`currency.${service.currencyCode}`)}/${this.atService.instant('common.store')}-${this.atService.instant('common.day')}` :this.atService.instant('common.free')
        : '';
    }
  }

  decodeSubscriptionStatusName(subscription: ISubscription): string {
    const subscriptionEndDate = moment(subscription.dateEnd);
    const today= moment();
    switch (subscription.status) {
      case 'ACTIVE':
        if (today.isAfter(subscriptionEndDate,'day')) {
          return `${this.atService.instant('marketplace.subscriptions.statusExpired')} ${subscriptionEndDate.format('DD MMM YYYY')}`;
        } else {
          return `${this.atService.instant('marketplace.subscriptions.statusActiveTill')} ${subscriptionEndDate.format('DD MMM YYYY')}`;
        }
      case 'EXPIRED':
        return this.atService.instant('marketplace.subscriptions.statusExpired');
      case 'CANCELED':
        return this.atService.instant('marketplace.subscriptions.statusCanceled');
      default:
        return this.atService.instant('marketplace.subscriptions.statusUndefined');
    }
  }

  decodeSubscriptionStatusColor(subscription: ISubscription): string {
    const subscriptionEndDate = moment(subscription.dateEnd);
    const today= moment();
    switch (subscription.status) {
      case 'ACTIVE':
        if (today.isAfter(subscriptionEndDate,'day')) {
          return 'red';
        } else {
          return 'green';
        }
      case 'EXPIRED':
        return 'red';
      case 'CANCELED':
        return 'red';
      default:
        return 'gray';
    }
  }
  canEditService(service: IService):boolean {
    if (!service.isDataLoaded) return false;
    if (!service.id) return true;
    else {
  //    if (service.status === 'DRAFT' && service.ownerPartyId === this.presenter.currentPartyId) return true;
      if (service.ownerPartyId === this.presenter.currentPartyId) return true;

      else return false;
    }
  }
  mapConnectingServiceToSubscription(item: IPlatformConnectionRequestServiceConnecting): ISubscription {
    const result: ISubscription = {
      ...emptySubscription,
      id: '',
      dateCreate: moment().toDate(),
      dateStart: moment(item.date_subscription_start,'YYYYMMDD').toDate(),
      dateEnd: moment(item.date_subscription_end,'YYYYMMDD').toDate(),
      status: 'ACTIVE',
      subscriberPartyId: '',
      subscriberPartyName: '',
      request: {...emptySubscriptionRequest},
      service: {...emptyService,id: item.id, name: item.name, code: item.code},
      profile: null,
      resources: Array.from([]),
      data: Array.from([])
    };
    item.attributes.forEach(t=>{
      result.data.push({
        code: t.type_code,
        level: 0,
        name: '',
        values: t.attributes.map(v=>({...emptyMDMElement, code: v.code, name: v.value}))
      });
    });
    return result;

  }
  decodeServiceType(type: TServiceType): string {
    switch (type) {
      case  'UNDEFINED': return 'Не определен'
      case  'INSIGHT': return 'Аналитический сервис'
      case  'API': return 'API'
      case  'MDM': return 'Мастер данные'
      case  'APPLICATION': return 'Приложения'
      default: return 'Не определен'

    }
  }
  decodeServiceStatus(status: TServiceStatus): string {
    switch (status) {
      case "UNDEFINED": return 'Не определен'
      case "DRAFT": return 'Черновик'
      case "ACTIVE": return 'Активный'
      case "ARCHIVE": return 'Архивный'
      default: return 'Другое'
    }
  }
  decodeServiceStatusColor(status: TServiceStatus): string {
    switch (status) {
      case "UNDEFINED": return 'gray'
      case "DRAFT": return 'blue'
      case "ACTIVE": return 'green'
      case "ARCHIVE": return 'red'
      default: return 'gray'
    }
  }
}
